import React from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material'

import { basicTableStyles } from './basic-table.styles'

const defaultKey = (i, row) => i

/**
 * @typedef {object} BasicTableColumnConfig
 * @property {string} [key] - Nombre del campo en el objeto de datos de la fila.
 * @property {'left' | 'center' | 'right'} [align='left'] - Alineación del contenido de la celda.
 * @property {string} label - Etiqueta que se muestra en el encabezado de la columna.
 * @property {'date' | 'datetime' | 'component' | 'user' | 'default'} [type] - Tipo de dato para formateo especial.
 * @property {React.ComponentType} [component] - Componente personalizado para renderizar el valor (cuando type='component').
 */

/**
 * @typedef {object} BasicTableProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {function(number, object): string | number} [rowKey] - Función para generar claves únicas para las filas.
 * @property {BasicTableColumnConfig[]} config - Array de configuraciones de columnas de la tabla.
 * @property {object[]} data - Array de objetos con los datos de las filas.
 * @property {any} [context] - Contexto adicional pasado a componentes personalizados de columna.
 * @property {any} [getdata] - Prop recibida pero no utilizada actualmente.
 * @property {'small' | 'medium'} [size='medium'] - Tamaño de las celdas de la tabla.
 * @property {function(object): void} [onClick] - Callback ejecutado al hacer clic en una fila. Si está presente, las filas son clickeables.
 * @property {boolean} [loading] - Si es true y hay onClick, muestra las filas en estado de carga.
 */

/**
 * Un componente de tabla básica con formateo automático de datos según tipo.
 *
 * Renderiza una tabla HTML semántica con encabezados y filas de datos. Soporta formateo
 * automático de fechas, fechas con hora, componentes personalizados, y datos de usuario.
 * Las filas pueden ser clickeables con efectos hover, y la tabla puede mostrar estado de carga.
 *
 * @component
 * @example
 * const MyTable = () => {
 *   const config = [
 *     { key: 'nombre', label: 'Nombre', align: 'left' },
 *     { key: 'fecha', label: 'Fecha', type: 'date', align: 'center' },
 *     { key: 'precio', label: 'Precio', align: 'right' }
 *   ];
 *
 *   const data = [
 *     { nombre: 'Producto 1', fecha: '2025-10-20', precio: '100€' },
 *     { nombre: 'Producto 2', fecha: '2025-10-21', precio: '50€' }
 *   ];
 *
 *   const handleRowClick = (row) => {
 *     console.log('Fila clickeada:', row);
 *   };
 *
 *   return (
 *     <BasicTable
 *       config={config}
 *       data={data}
 *       onClick={handleRowClick}
 *       size="small"
 *     />
 *   );
 * };
 *
 * @param {BasicTableProps} props - Las props del componente BasicTable.
 */
export const BasicTable = withStyles(basicTableStyles)(({
  rowKey = defaultKey,
  classes,
  config,
  data,
  context,
  getdata,
  size = 'medium',
  ...props
}) => {
  const { formatDate, formatTime } = useIntl()

  const getParsedValue = (type, value, Component, context, row) => {
    switch (type) {
      case 'date':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value))}
          </Typography>
        )
      case 'datetime':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))}
          </Typography>
        )
      case 'component':
        return <Component value={value} context={context} row={row} />
      case 'user':
        const finalValue = row[value] ? `${row[value]} (${row['rol_usuario']})` : null
        return finalValue

      default:
        return value
    }
  }
  return (
    <TableContainer>
      <Table className={classes.table} aria-label="simple table" size={size}>
        <TableHead>
          <TableRow>
            {config.map(({ align = 'left', label }, i) => (
              <TableCell key={i} align={align}>
                <Typography variant="subtitle1">{label}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow
              key={rowKey(i, row)}
              className={
                props.onClick
                  ? props.loading
                    ? classes.loadingRow
                    : classes.clickableRow
                  : classes.row
              }
              hover={props.onClick}
              onClick={() => (props.onClick ? props.onClick(row) : null)}
            >
              {config.map(({ key, align = 'left', type, component }, i) => (
                <TableCell key={i} align={align}>
                  {getParsedValue(type, row[key], component, context, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
